"use client";

import { Trans } from "@lingui/react/macro";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MinusIcon,
} from "lucide-react";
import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { UserAnalyticsViewPresetID } from "../_utils/preset-config";

// shadcn/ui Checkboxと同じスタイルのカスタムコンポーネント
const ShadcnCheckbox = React.memo(
  ({
    checked,
    indeterminate = false,
    className,
    ...props
  }: {
    checked: boolean;
    indeterminate?: boolean;
    className?: string;
  }) => {
    return (
      <div
        className={cn(
          "group peer border-input dark:bg-input/30",
          checked &&
            "bg-primary text-primary-foreground dark:bg-primary border-primary",
          indeterminate &&
            "bg-primary text-primary-foreground dark:bg-primary border-primary",
          "size-3.5 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
          "flex items-center justify-center",
          className,
        )}
        {...props}
      >
        {checked && !indeterminate && (
          <CheckIcon className="size-3 text-current" />
        )}
        {indeterminate && <MinusIcon className="size-3 text-current" />}
      </div>
    );
  },
);
ShadcnCheckbox.displayName = "ShadcnCheckbox";

interface CategoryTreeProps {
  categories: Record<
    string,
    {
      label: string;
      color: string;
      presets: Array<{
        presetID: UserAnalyticsViewPresetID;
        name: string;
        description?: string;
      }>;
    }
  >;
  selectedPresetIds: UserAnalyticsViewPresetID[];
  expandedCategories: string[];
  onPresetToggle: (presetId: UserAnalyticsViewPresetID) => void;
  onCategoryToggle: (category: string) => void;
  onSelectAll: () => void;
  viewPresets: Array<{
    presetID: UserAnalyticsViewPresetID;
    name: string;
    description?: string;
  }>;
}

export const CategoryTree = React.memo(function CategoryTree({
  categories,
  selectedPresetIds,
  expandedCategories,
  onPresetToggle,
  onCategoryToggle,
  onSelectAll,
  viewPresets,
}: CategoryTreeProps) {
  const handleCategorySelect = useCallback(
    (categoryKey: string) => {
      const category = categories[categoryKey];
      if (!category) return;

      const categoryPresetIds = category.presets.map((p) => p.presetID);
      const allSelected = categoryPresetIds.every((id) =>
        selectedPresetIds.includes(id),
      );

      if (allSelected) {
        // Deselect all in category
        categoryPresetIds.forEach((id) => {
          if (selectedPresetIds.includes(id)) {
            onPresetToggle(id);
          }
        });
      } else {
        // Select all in category
        categoryPresetIds.forEach((id) => {
          if (!selectedPresetIds.includes(id)) {
            onPresetToggle(id);
          }
        });
      }
    },
    [categories, selectedPresetIds, onPresetToggle],
  );

  return (
    <div className="h-full overflow-hidden flex flex-col border rounded-l-lg">
      <div className="h-[46px] px-3 border-b bg-muted/50 flex items-center flex-shrink-0">
        <div className="flex items-center justify-between w-full">
          <span className="text-xs font-semibold text-foreground">
            <Trans>Category</Trans>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onSelectAll}
            className="h-6 text-xs px-2"
          >
            {selectedPresetIds.length === viewPresets.length ? (
              <Trans>Deselect All</Trans>
            ) : (
              <Trans>Select All</Trans>
            )}
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="py-2 px-1">
          {/* 全体 */}
          <div
            className="flex items-center space-x-2 px-3 py-2 mx-1 rounded hover:bg-muted/50 w-[calc(100%-8px)] text-left cursor-pointer"
            onClick={onSelectAll}
            onKeyDown={(e) => e.key === "Enter" && onSelectAll()}
            role="button"
            tabIndex={0}
          >
            <ShadcnCheckbox
              checked={selectedPresetIds.length === viewPresets.length}
              className="h-3.5 w-3.5"
            />
            <span
              className={`text-xs ${
                selectedPresetIds.length === viewPresets.length
                  ? "font-semibold text-foreground"
                  : "font-medium text-foreground/70"
              }`}
            >
              <Trans>All</Trans> ({selectedPresetIds.length}/
              {viewPresets.length})
            </span>
          </div>

          {/* カテゴリー別 */}
          <div className="mt-1 space-y-0.5">
            {Object.entries(categories).map(([key, category]) => {
              const isExpanded = expandedCategories.includes(key);
              const categoryPresetIds = category.presets.map((p) => p.presetID);
              const selectedCount = categoryPresetIds.filter((id) =>
                selectedPresetIds.includes(id),
              ).length;
              const isPartiallySelected =
                selectedCount > 0 && selectedCount < categoryPresetIds.length;
              const allSelected = selectedCount === categoryPresetIds.length;

              return (
                <div key={key}>
                  <div
                    className="flex items-center space-x-2 px-3 py-2 mx-1 rounded hover:bg-muted/50 w-[calc(100%-8px)] text-left cursor-pointer"
                    onClick={() => handleCategorySelect(key)}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCategoryToggle(key);
                      }}
                      className="p-0 hover:bg-transparent flex-shrink-0"
                    >
                      {isExpanded ? (
                        <ChevronDownIcon className="h-3 w-3" />
                      ) : (
                        <ChevronRightIcon className="h-3 w-3" />
                      )}
                    </button>
                    <ShadcnCheckbox
                      checked={allSelected}
                      indeterminate={isPartiallySelected}
                      className="h-3.5 w-3.5"
                    />
                    <span
                      className={`text-xs truncate ${
                        allSelected
                          ? "font-semibold text-foreground"
                          : isPartiallySelected
                            ? "font-medium text-foreground/90"
                            : "font-normal text-foreground/70"
                      }`}
                    >
                      {category.label} ({selectedCount}/
                      {category.presets.length})
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="ml-6 space-y-0.5">
                      {category.presets.map((preset) => {
                        const isSelected = selectedPresetIds.includes(
                          preset.presetID,
                        );
                        return (
                          <div
                            key={preset.presetID}
                            className="flex items-center space-x-2 px-3 py-1.5 pl-7 mx-1 rounded hover:bg-muted/30 w-[calc(100%-8px)] text-left cursor-pointer"
                            onClick={() => onPresetToggle(preset.presetID)}
                          >
                            <ShadcnCheckbox
                              checked={isSelected}
                              className="h-3.5 w-3.5"
                            />
                            <span
                              className={`text-xs truncate ${
                                isSelected
                                  ? "font-medium text-foreground"
                                  : "font-normal text-foreground/70"
                              }`}
                            >
                              {preset.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});
